import { endOfDay, parseISO, startOfDay } from "date-fns"
import { DriverDisplayInfo } from "@/reducers/accountChatsSelectors"
import { RouteStop, shouldAlert } from "@/reducers/routes2gSlice"
import {
  firstPickupStop,
  lastDeliveryStop,
  PlannedOrder,
} from "@/reducers/plannedOrdersSlice"

export type VehicleSuggestion = {
  chatJid: string
  driverName: string
  plates?: string
  // Where the vehicle finishes its last delivery before the order's pickup
  // time (falls back to any stop with coordinates, then current GPS position).
  referenceLabel?: string
  // Skąd liczony jest distanceKm. Potrzebne, żeby dojazd drogowy startował
  // dokładnie z tego samego miejsca, które opisuje referenceLabel.
  referenceCoordinates?: { lat: number; lon: number }
  // Straight-line km from the reference position to the order pickup;
  // undefined when either side lacks coordinates.
  distanceKm?: number
  // When the vehicle becomes free before the order pickup.
  freeFrom?: Date
  hasFreeWindow: boolean
  // The vehicle already has an upcoming stop on its current route flagged
  // "late" (route2g's own live-position ETA, already synced — no extra
  // gbox/navigator request). A dispatcher should know this before handing
  // them another job.
  hasLateStop: boolean
  // The vehicle's busy time, exposed so the UI can render the suggestion as a
  // mini timeline row (per the planner scope doc).
  busyIntervals: BusyInterval[]
}

export type BusyInterval = {
  start: Date
  end: Date
  // Where the work starts/ends — lets the UI show "finishes in [flag]"
  // before an order window and "next starts in [flag]" after it.
  startCountryCode?: string
  endCountryCode?: string
}

export type ProximityPoint = { lat: number; lon: number; label?: string }

export type SuggestionCandidate = {
  driver: DriverDisplayInfo
  busyIntervals: BusyInterval[]
  // Planned orders already assigned to this vehicle — their deliveries count
  // as reference positions (a chained order starts where the previous planned
  // one unloads, not where the truck is now).
  assignedPlannedOrders?: PlannedOrder[]
}

type ReferenceCandidate = {
  time: Date
  coordinates: { lat: number; lon: number }
  label?: string
  isDelivery: boolean
}

const EARTH_RADIUS_KM = 6371

const toRadians = (degrees: number) => (degrees * Math.PI) / 180

const haversineKm = (
  latA: number,
  lonA: number,
  latB: number,
  lonB: number,
): number => {
  const dLat = toRadians(latB - latA)
  const dLon = toRadians(lonB - lonA)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(latA)) *
      Math.cos(toRadians(latB)) *
      Math.sin(dLon / 2) ** 2
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a))
}

const parseTime = (value?: string): Date | undefined => {
  if (!value) return undefined
  const parsed = parseISO(value)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}

// Współrzędne w danych są stringami i nie muszą być liczbami. Jedno miejsce na
// ten wybór, bo fallback na live GPS sprawdzał kiedyś tylko truthiness — a
// Number("brak") to NaN, czyli "NaN km" pod tablicami i {"lat":null} na drucie.
const locationCoordinates = (
  location: { lat?: string; lon?: string } | undefined,
): { lat: number; lon: number } | undefined => {
  if (!location) return undefined
  const lat = Number(location.lat)
  const lon = Number(location.lon)
  if (Number.isNaN(lat) || Number.isNaN(lon) || (!lat && !lon)) {
    return undefined
  }
  return { lat, lon }
}

const stopCoordinates = (
  stop: RouteStop | undefined,
): { lat: number; lon: number } | undefined =>
  stop ? locationCoordinates(stop.location) : undefined

const overlaps = (interval: BusyInterval, start: Date, end: Date): boolean =>
  interval.start < end && interval.end > start

// Synced stops often carry a country name but no ISO code — map the common
// ones (Polish and English spellings) so flags still render.
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  polska: "PL",
  poland: "PL",
  niemcy: "DE",
  germany: "DE",
  deutschland: "DE",
  czechy: "CZ",
  czechia: "CZ",
  "czech republic": "CZ",
  francja: "FR",
  france: "FR",
  holandia: "NL",
  netherlands: "NL",
  niderlandy: "NL",
  austria: "AT",
  belgia: "BE",
  belgium: "BE",
  wlochy: "IT",
  włochy: "IT",
  italy: "IT",
  hiszpania: "ES",
  spain: "ES",
  dania: "DK",
  denmark: "DK",
  szwecja: "SE",
  sweden: "SE",
  slowacja: "SK",
  słowacja: "SK",
  slovakia: "SK",
  wegry: "HU",
  węgry: "HU",
  hungary: "HU",
  litwa: "LT",
  lithuania: "LT",
  szwajcaria: "CH",
  switzerland: "CH",
  luksemburg: "LU",
  luxembourg: "LU",
}

const countryNameToCode = (name?: string): string | undefined =>
  name ? COUNTRY_NAME_TO_CODE[name.trim().toLowerCase()] : undefined

export const resolveCountryCode = (
  location: RouteStop["location"] | undefined,
): string | undefined => {
  if (!location) return undefined
  if (location.countryCode && /^[A-Za-z]{2}$/.test(location.countryCode)) {
    return location.countryCode.toUpperCase()
  }
  const fromCountry = countryNameToCode(location.country)
  if (fromCountry) return fromCountry
  // Last resort: addresses usually end with ", <country>"
  const lastToken = location.formattedAddress?.split(",").pop()
  return countryNameToCode(lastToken)
}

// Co odróżnia dwie ścieżki rankingu: punkt, do którego mierzymy, granica
// czasu dla pozycji odniesienia i okno, które pojazd musi mieć wolne. Reszta
// — kaskada odniesienia i kształt VehicleSuggestion — jest wspólna, i musi
// zostać wspólna: to jedyna reguła mówiąca "gdzie ten pojazd wtedy będzie".
type RankingTarget = {
  coordinates?: { lat: number; lon: number }
  // Pozycją odniesienia może być tylko stop nie późniejszy niż to.
  referenceBefore?: Date
  // Kolizja z tym oknem znaczy "zajęty" (hasFreeWindow === false).
  window?: { start: Date; end: Date }
}

const buildSuggestion = (
  candidate: SuggestionCandidate,
  target: RankingTarget,
): VehicleSuggestion | undefined => {
  const { driver, busyIntervals, assignedPlannedOrders } = candidate
  if (!driver.chatJid) return undefined

  const targetWindow = target.window
  const hasFreeWindow =
    !targetWindow ||
    !busyIntervals.some((interval) =>
      overlaps(interval, targetWindow.start, targetWindow.end),
    )

  const boundary = target.referenceBefore
  const intervalsBeforeBoundary = boundary
    ? busyIntervals.filter((interval) => interval.end <= boundary)
    : busyIntervals
  const freeFrom =
    intervalsBeforeBoundary.length > 0
      ? intervalsBeforeBoundary.reduce(
          (latest, interval) => (interval.end > latest ? interval.end : latest),
          intervalsBeforeBoundary[0].end,
        )
      : undefined

  // Pozycja odniesienia: ostatni ROZŁADUNEK przed granicą — z bieżącej trasy
  // ORAZ z planowanych zleceń już przypisanych do pojazdu (zlecenie w łańcuchu
  // startuje tam, gdzie poprzednie planowane się rozładowuje). Fallback: dowolny
  // stop z koordynatami, potem live GPS.
  const referenceCandidates: ReferenceCandidate[] = []

  for (const stop of driver.stops ?? []) {
    const time =
      parseTime(stop.scheduled_arrival_time) ??
      parseTime(stop.estimated_arrival_time)
    const coordinates = stopCoordinates(stop)
    if (!time || !coordinates || (boundary && time > boundary)) continue
    referenceCandidates.push({
      time,
      coordinates,
      label: stop.location.locality ?? stop.location.formattedAddress,
      isDelivery: stop.action === "delivery",
    })
  }

  for (const assignedOrder of assignedPlannedOrders ?? []) {
    const plannedDelivery = lastDeliveryStop(assignedOrder)
    const time = parseTime(plannedDelivery?.scheduled_arrival_time)
    const coordinates = stopCoordinates(plannedDelivery)
    if (!time || !coordinates || (boundary && time > boundary)) continue
    referenceCandidates.push({
      time,
      coordinates,
      label:
        plannedDelivery?.location.locality ??
        plannedDelivery?.location.formattedAddress,
      isDelivery: true,
    })
  }

  const latestBy = (entries: ReferenceCandidate[]) =>
    entries.reduce<ReferenceCandidate | undefined>(
      (latest, entry) => (!latest || entry.time > latest.time ? entry : latest),
      undefined,
    )
  const reference =
    latestBy(referenceCandidates.filter((entry) => entry.isDelivery)) ??
    latestBy(referenceCandidates)

  const referenceCoordinates =
    reference?.coordinates ?? locationCoordinates(driver.location)
  const referenceLabel =
    reference?.label ??
    driver.location.locality ??
    driver.location.formattedAddress

  const distanceKm =
    target.coordinates && referenceCoordinates
      ? haversineKm(
          referenceCoordinates.lat,
          referenceCoordinates.lon,
          target.coordinates.lat,
          target.coordinates.lon,
        )
      : undefined

  return {
    chatJid: driver.chatJid,
    driverName: driver.name,
    plates: driver.plates,
    referenceLabel,
    referenceCoordinates,
    distanceKm,
    freeFrom,
    hasFreeWindow,
    hasLateStop: (driver.stops ?? []).some(shouldAlert),
    busyIntervals,
  }
}

// Busy intervals of a vehicle derived from its current route stops and its
// already-assigned planned orders.
export const buildBusyIntervals = (
  driver: DriverDisplayInfo,
  plannedOrdersForVehicle: PlannedOrder[],
): BusyInterval[] => {
  const intervals: BusyInterval[] = []

  const timedStops = (driver.stops ?? [])
    .map((stop) => ({
      stop,
      time:
        parseTime(stop.scheduled_arrival_time) ??
        parseTime(stop.estimated_arrival_time_with_tacho) ??
        parseTime(stop.estimated_arrival_time),
    }))
    .filter((entry): entry is { stop: RouteStop; time: Date } =>
      Boolean(entry.time),
    )
    .sort((a, b) => a.time.getTime() - b.time.getTime())
  if (timedStops.length > 0) {
    const first = timedStops[0]
    const last = timedStops[timedStops.length - 1]
    intervals.push({
      start: first.time,
      end: last.time,
      startCountryCode: resolveCountryCode(first.stop.location),
      endCountryCode: resolveCountryCode(last.stop.location),
    })
  }

  for (const order of plannedOrdersForVehicle) {
    const pickup = firstPickupStop(order)
    const delivery = lastDeliveryStop(order)
    const pickupTime = parseTime(pickup?.scheduled_arrival_time)
    const deliveryTime = parseTime(delivery?.scheduled_arrival_time)
    if (!pickupTime) continue
    const hasRange = deliveryTime !== undefined && pickupTime < deliveryTime
    intervals.push({
      start: pickupTime,
      end: hasRange ? deliveryTime : pickupTime,
      startCountryCode: resolveCountryCode(pickup?.location),
      endCountryCode: hasRange
        ? resolveCountryCode(delivery?.location)
        : resolveCountryCode(pickup?.location),
    })
  }

  return intervals
}

// v1 of the Navigator-timeline heuristic: vehicles with a free window between
// the order's pickup and delivery, sorted by distance of their last position
// (last stop with coordinates before pickup, else current GPS location) to the
// pickup place. Swappable — later versions may add driver working times etc.
export const suggestVehiclesForOrder = (
  order: PlannedOrder,
  candidates: SuggestionCandidate[],
): VehicleSuggestion[] => {
  const pickup = firstPickupStop(order)
  const delivery = lastDeliveryStop(order)
  const pickupTime = parseTime(pickup?.scheduled_arrival_time)
  const deliveryTime = parseTime(delivery?.scheduled_arrival_time) ?? pickupTime
  const target: RankingTarget = {
    coordinates: stopCoordinates(pickup),
    referenceBefore: pickupTime,
    window:
      pickupTime && deliveryTime
        ? { start: pickupTime, end: deliveryTime }
        : undefined,
  }

  const suggestions = candidates
    .map((candidate) => buildSuggestion(candidate, target))
    .filter((suggestion): suggestion is VehicleSuggestion =>
      Boolean(suggestion),
    )

  return suggestions.sort((left, right) => {
    if (left.hasFreeWindow !== right.hasFreeWindow) {
      return left.hasFreeWindow ? -1 : 1
    }
    if (left.distanceKm !== undefined && right.distanceKm !== undefined) {
      return left.distanceKm - right.distanceKm
    }
    if (left.distanceKm !== undefined) return -1
    if (right.distanceKm !== undefined) return 1
    return left.driverName.localeCompare(right.driverName)
  })
}

// Ranking "które auto jest najbliżej tego punktu tego dnia" — odpowiednik
// "Punktu planowania" z Marcosa. Pozycja odniesienia to ta sama kaskada, którą
// stosuje suggestVehiclesForOrder, tylko z granicą na końcu wybranego dnia:
// interesuje nas, gdzie pojazd skończy pracę przed tym dniem.
//
// Sortowanie jest czysto odległościowe — zajętość w wybranym dniu wraca w
// hasFreeWindow jako znacznik, ale nie przesuwa pojazdu w dół. Dyspozytor
// decyduje sam, czy przeplanować.
export const rankVehiclesByProximity = (
  point: ProximityPoint,
  day: Date,
  candidates: SuggestionCandidate[],
): VehicleSuggestion[] => {
  const target: RankingTarget = {
    coordinates: { lat: point.lat, lon: point.lon },
    referenceBefore: endOfDay(day),
    window: { start: startOfDay(day), end: endOfDay(day) },
  }

  const suggestions = candidates
    .map((candidate) => buildSuggestion(candidate, target))
    .filter((suggestion): suggestion is VehicleSuggestion =>
      Boolean(suggestion),
    )

  return suggestions.sort((left, right) => {
    // Compare distances
    if (left.distanceKm !== undefined && right.distanceKm !== undefined) {
      const distanceDiff = left.distanceKm - right.distanceKm
      if (distanceDiff !== 0) return distanceDiff
      // Fall through to tiebreaker if equal
    } else if (left.distanceKm !== undefined) {
      return -1
    } else if (right.distanceKm !== undefined) {
      return 1
    }
    // Tiebreaker: sort by plates or driverName
    return (left.plates || left.driverName).localeCompare(
      right.plates || right.driverName,
    )
  })
}
